<script setup>
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import TfPopupShell from './TfPopupShell.vue';
import ContatoDrawer from './ContatoDrawer.vue';
import { usePopupToast } from './usePopupToast';
import { CORES } from './crmMap.js';
import {
  mapearFunil,
  mapearContato,
  montarListaFunis,
  formatarTelefone,
  temNome,
  casaBusca,
  LIMITE_CARDS,
} from './crmMap.js';
import {
  apiCrmPipelines,
  apiCrmBoard,
  apiCrmColunaCreate,
  apiCrmColunaPatch,
  apiCrmColunaDelete,
  apiCrmColunaReorder,
  apiCrmContatoCreate,
  apiCrmContatoGet,
  apiCrmContatoPatch,
  apiCrmContatoDelete,
  apiCrmContatoMover,
  apiCrmPipelineCreate,
  apiCrmPipelinePatch,
  apiCrmPipelineDelete,
  apiCrmTags,
  apiCrmTagCreate,
  apiCrmCampos,
  apiCrmBuscarPlataforma,
  apiCrmContatoDeleteMany,
  apiCrmJob,
} from './crmApi';

const emit = defineEmits(['close']);
const { toast, avisar } = usePopupToast();
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const pipelines = reactive([]);
const pipeAtivoId = ref('');
const carregando = ref(true);
const recarregando = ref(false);
const pipe = computed(
  () => pipelines.find(p => p.id === pipeAtivoId.value) || pipelines[0] || { id: '', nome: '', colunas: [] }
);

let recarregarSeq = 0;
const recarregar = async () => {
  const mine = ++recarregarSeq;
  recarregando.value = true;
  try {
    const acc = Number(accountId.value);
    const user = currentUser.value;
    // Pipelines primeiro — nunca pedir board sem pipelineId (mistura funis e faz a UI piscar).
    const dataPipes = await apiCrmPipelines(acc, user);
    const pipes = dataPipes.pipelines || [];
    if (!pipes.length) {
      if (mine !== recarregarSeq) return;
      pipelines.splice(0, pipelines.length);
      pipeAtivoId.value = '';
      return;
    }
    const pid =
      (pipes.some(p => String(p.id) === String(pipeAtivoId.value)) &&
        pipeAtivoId.value) ||
      pipes.find(p => p.principal || p.padrao)?.id ||
      pipes[0].id;
    if (mine !== recarregarSeq) return;
    if (String(pipeAtivoId.value || '') !== String(pid)) {
      pipeAtivoId.value = pid;
    }
    const board = await apiCrmBoard(acc, user, pid, {
      limit: LIMITE_CARDS,
      q: busca.value,
    });
    if (mine !== recarregarSeq) return;
    const funil = mapearFunil(
      pipes.find(p => String(p.id) === String(pid)) || pipes[0],
      board
    );
    const lista = montarListaFunis(pipes, funil);
    pipelines.splice(0, pipelines.length, ...lista);
    pipeAtivoId.value = funil.id || pid;
  } finally {
    if (mine === recarregarSeq) recarregando.value = false;
  }
};

watch(pipeAtivoId, async (id, prev) => {
  if (!id || id === prev || carregando.value || recarregando.value) return;
  const atual = pipelines.find(p => p.id === id);
  if (atual?.colunas?.length) return;
  try {
    await recarregar();
  } catch (e) {
    avisar(e.message);
  }
});


const VIEWS = [
  { id: 'kanban', label: 'Kanban', icon: 'i-lucide-layout-grid' },
  { id: 'lista', label: 'Lista', icon: 'i-lucide-list' },
  { id: 'linhas', label: 'Linhas', icon: 'i-lucide-rows-2' },
  { id: 'funil', label: 'Funil', icon: 'i-lucide-arrow-down-up' },
];
const view = ref('kanban');
const fullscreen = ref(false);
const zoom = ref(1);
const mudarZoom = delta => {
  zoom.value = Math.min(1.3, Math.max(0.6, +(zoom.value + delta).toFixed(1)));
};

const busca = ref('');
let buscaTimer;
watch(busca, () => {
  if (carregando.value) return;
  clearTimeout(buscaTimer);
  buscaTimer = setTimeout(() => {
    recarregar().catch(e => avisar(e.message));
  }, 280);
});
const filtro = reactive({ ocultas: [], soComNome: false });
const filtroAtivo = computed(
  () => filtro.ocultas.length > 0 || filtro.soComNome
);
const limparFiltro = () => {
  filtro.ocultas = [];
  filtro.soComNome = false;
};
const toggleOculta = id => {
  filtro.ocultas = filtro.ocultas.includes(id)
    ? filtro.ocultas.filter(x => x !== id)
    : [...filtro.ocultas, id];
};

const casa = c => casaBusca(c, busca.value, filtro.soComNome);
const colunasVisiveis = computed(() =>
  (pipe.value.colunas || []).filter(col => !filtro.ocultas.includes(col.id))
);
const visiveisTodos = col => (col.contatos || []).filter(casa);
const visiveisCount = col => {
  if (filtro.soComNome) return visiveisTodos(col).length;
  return Number(col.total) || visiveisTodos(col).length;
};
const visiveis = col => visiveisTodos(col);
const restoColuna = col => Math.max(0, visiveisCount(col) - visiveis(col).length);
const mostrarMais = async col => {
  try {
    const extra = await apiCrmBoard(
      Number(accountId.value),
      currentUser.value,
      undefined,
      {
        limit: LIMITE_CARDS,
        offset: (col.contatos || []).length,
        colunaId: col.id,
        q: busca.value,
      }
    );
    const ids = new Set((col.contatos || []).map(c => c.id));
    (extra.contatos || []).forEach(c => {
      const m = mapearContato(c);
      if (m.id && !ids.has(m.id)) {
        col.contatos.push(m);
        ids.add(m.id);
      }
    });
    if (extra.totais) {
      col.total =
        extra.totais[col.id] ?? extra.totais[String(col.id)] ?? col.total;
    }
  } catch (e) {
    avisar(e.message);
  }
};
const todosVisiveis = computed(() =>
  colunasVisiveis.value.flatMap(col =>
    visiveis(col).map(c => ({ contato: c, coluna: col }))
  )
);
const maiorColuna = computed(() =>
  Math.max(1, ...colunasVisiveis.value.map(col => visiveisCount(col)))
);
const formatarData = ts => {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const menu = ref(null);
const fecharMenus = () => {
  menu.value = null;
};
const abrirMenu = id => {
  menu.value = menu.value === id ? null : id;
};

const drag = ref(null);
const sobreColuna = ref(null);
const onCardDragStart = (e, col, ct) => {
  drag.value = { tipo: 'card', colId: col.id, ctId: ct.id };
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', ct.id);
};
const onColDragStart = (e, col) => {
  drag.value = { tipo: 'coluna', colId: col.id };
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', col.id);
};
const onDragOver = (e, col) => {
  if (!drag.value) return;
  e.preventDefault();
  sobreColuna.value = col.id;
};
const onDragEnd = () => {
  drag.value = null;
  sobreColuna.value = null;
};
const acharColuna = id => (pipe.value.colunas || []).find(c => c.id === id);

const moverContato = (ctId, deId, paraId, antesDeId = null) => {
  const de = acharColuna(deId);
  const para = acharColuna(paraId);
  if (!de || !para) return;
  const idx = de.contatos.findIndex(c => c.id === ctId);
  if (idx < 0) return;
  const [ct] = de.contatos.splice(idx, 1);
  const alvo = antesDeId ? para.contatos.findIndex(c => c.id === antesDeId) : -1;
  if (alvo >= 0) para.contatos.splice(alvo, 0, ct);
  else para.contatos.push(ct);
};

const onDrop = async (e, col, antesDe = null) => {
  e.preventDefault();
  const d = drag.value;
  onDragEnd();
  if (!d) return;
  if (d.tipo === 'card') {
    if (antesDe?.id === d.ctId) return;
    moverContato(d.ctId, d.colId, col.id, antesDe?.id);
    if (d.colId !== col.id) {
      try {
        await apiCrmContatoMover(Number(accountId.value), currentUser.value, d.ctId, col.id);
        avisar(`Contato movido para "${col.titulo}"`);
      } catch (err) {
        avisar(err.message);
        await recarregar().catch(() => {});
      }
    }
    return;
  }
  const cols = pipe.value.colunas;
  const de = cols.findIndex(c => c.id === d.colId);
  const para = cols.findIndex(c => c.id === col.id);
  if (de < 0 || para < 0 || de === para) return;
  const [moved] = cols.splice(de, 1);
  cols.splice(para, 0, moved);
  try {
    await apiCrmColunaReorder(
      Number(accountId.value),
      currentUser.value,
      d.colId,
      col.id
    );
  } catch (err) {
    avisar(err.message);
    await recarregar().catch(() => {});
  }
};

const novaColuna = async () => {
  try {
    await apiCrmColunaCreate(Number(accountId.value), currentUser.value, {
      titulo: 'Nova Coluna',
      cor: CORES[(pipe.value.colunas || []).length % CORES.length],
      pipelineId: pipeAtivoId.value,
    });
    await recarregar();
    avisar('Coluna criada no CRM de produção');
  } catch (e) {
    avisar(e.message);
  }
};
const renomeando = ref(null);
const tituloEdit = ref('');
const iniciarRenomear = col => {
  renomeando.value = col.id;
  tituloEdit.value = col.titulo;
  fecharMenus();
};
const confirmarRenomear = async col => {
  const t = tituloEdit.value.trim();
  renomeando.value = null;
  if (!t || t === col.titulo) return;
  const anterior = col.titulo;
  col.titulo = t;
  try {
    await apiCrmColunaPatch(Number(accountId.value), currentUser.value, col.id, {
      titulo: t,
    });
  } catch (e) {
    col.titulo = anterior;
    avisar(e.message);
  }
};
const moverColuna = async (col, delta) => {
  const cols = pipe.value.colunas;
  const i = cols.indexOf(col);
  const j = i + delta;
  if (j < 0 || j >= cols.length) return;
  const destino = cols[j];
  cols.splice(i, 1);
  cols.splice(j, 0, col);
  fecharMenus();
  try {
    await apiCrmColunaReorder(
      Number(accountId.value),
      currentUser.value,
      col.id,
      destino.id
    );
  } catch (e) {
    avisar(e.message);
    await recarregar().catch(() => {});
  }
};
const totalColuna = col =>
  Number(col?.total) || (col?.contatos || []).length || 0;

const excluirColuna = async col => {
  const cols = pipe.value.colunas;
  if (cols.length <= 1) {
    avisar('O funil precisa de pelo menos uma coluna');
    return;
  }
  const destino = cols.find(c => c.id !== col.id);
  const n = totalColuna(col);
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      n
        ? `Excluir "${col.titulo}" no CRM de produção?\nOs ${n} contato(s) vão para "${destino.titulo}".`
        : `Excluir "${col.titulo}" no CRM de produção?`
    )
  ) {
    return;
  }
  fecharMenus();
  const titulo = col.titulo;
  const idx = cols.findIndex(c => c.id === col.id);
  // Otimista: tira da UI na hora (evita flash de outro funil no reload).
  if (idx >= 0) cols.splice(idx, 1);
  try {
    await apiCrmColunaDelete(
      Number(accountId.value),
      currentUser.value,
      col.id,
      destino.id
    );
    await recarregar();
    avisar(`Coluna "${titulo}" excluída`);
  } catch (e) {
    avisar(e.message);
    await recarregar().catch(() => {});
  }
};

const adicionandoEm = ref(null);
const salvandoNovo = ref(false);
const novoContato = reactive({
  nome: '',
  telefone: '',
  jaNaPlataforma: false,
  plataformaId: null,
});
const buscaNovoPlataforma = ref('');
const resultadosNovoPlataforma = ref([]);
const buscandoNovoPlataforma = ref(false);
let novoPlatTimer;

const telDigitos = v => String(v || '').replace(/\D/g, '');
const telMatch = (a, b) => {
  const x = telDigitos(a);
  const y = telDigitos(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.startsWith('55') && x.slice(2) === y) return true;
  if (y.startsWith('55') && y.slice(2) === x) return true;
  const n = Math.min(11, x.length, y.length);
  return n >= 8 && x.slice(-n) === y.slice(-n);
};
const acharNoFunilPorTel = tel => {
  for (const col of pipe.value.colunas || []) {
    for (const ct of col.contatos || []) {
      if (telMatch(ct.telefone, tel)) return { col, ct };
    }
  }
  return null;
};

const resetNovoContato = () => {
  novoContato.nome = '';
  novoContato.telefone = '';
  novoContato.jaNaPlataforma = false;
  novoContato.plataformaId = null;
  buscaNovoPlataforma.value = '';
  resultadosNovoPlataforma.value = [];
};
const abrirAdicionar = col => {
  adicionandoEm.value = col.id;
  resetNovoContato();
};
const cancelarNovo = () => {
  adicionandoEm.value = null;
  resetNovoContato();
};
const modoPuxarChat = computed(
  () => !!novoContato.jaNaPlataforma && !!telDigitos(novoContato.telefone)
);
const digitarNovoPlataforma = () => {
  // Digitar na busca do chat não é seleção — limpa seleção anterior.
  if (novoContato.jaNaPlataforma) {
    novoContato.jaNaPlataforma = false;
    novoContato.plataformaId = null;
  }
  clearTimeout(novoPlatTimer);
  novoPlatTimer = setTimeout(async () => {
    const q = buscaNovoPlataforma.value.trim();
    if (q.length < 2) {
      resultadosNovoPlataforma.value = [];
      return;
    }
    buscandoNovoPlataforma.value = true;
    try {
      const r = await apiCrmBuscarPlataforma(Number(accountId.value), currentUser.value, q);
      resultadosNovoPlataforma.value = r.contatos || [];
    } catch (e) {
      avisar(e.message);
    } finally {
      buscandoNovoPlataforma.value = false;
    }
  }, 280);
};
const escolherDaPlataforma = (col, c) => {
  const tel = telDigitos(c.telefone);
  if (tel.length < 10) {
    avisar('Este contato precisa de um telefone para entrar no CRM');
    return;
  }
  novoContato.nome = c.nome || '';
  novoContato.telefone = tel;
  novoContato.jaNaPlataforma = true;
  novoContato.plataformaId = c.id || null;
  buscaNovoPlataforma.value = c.nome || tel;
  resultadosNovoPlataforma.value = [];
  // Não auto-salva: usuário confirma com o botão (evita 409 “fantasma”).
};
const onTelefoneNovoInput = () => {
  // Só invalida “puxar do chat” se o número deixar de bater com a seleção.
  if (!novoContato.jaNaPlataforma) return;
  const tel = telDigitos(novoContato.telefone);
  if (!tel || (novoContato.plataformaId && tel.length >= 10)) {
    // mantém jaNaPlataforma enquanto edita formatação; se apagar, cai pro modo criar
    if (!tel) {
      novoContato.jaNaPlataforma = false;
      novoContato.plataformaId = null;
    }
  }
};
const mensagemAposSalvar = r => {
  if (r?.jaExistia) {
    return r.movido
      ? 'Já estava no CRM — movi para esta etapa.'
      : 'Já estava no CRM nesta etapa.';
  }
  const plat = r?.plataforma;
  if (plat && plat.ok === false) {
    return 'Salvo no CRM. No chat não entrou: ' + (plat.erro || 'falhou');
  }
  if (plat?.ok) return 'Contato criado no CRM e no chat';
  if (novoContato.jaNaPlataforma || r?.jaNaPlataforma) return 'Contato puxado do chat para o CRM';
  return 'Contato adicionado ao CRM';
};
const salvarNovoContato = async col => {
  if (salvandoNovo.value) return;
  const tel = telDigitos(novoContato.telefone);
  if (tel.length < 10) {
    // Se digitou só na busca do chat e há exatamente 1 resultado, usa ele.
    if (resultadosNovoPlataforma.value.length === 1) {
      escolherDaPlataforma(col, resultadosNovoPlataforma.value[0]);
      return salvarNovoContato(col);
    }
    if (buscaNovoPlataforma.value.trim().length >= 2 && resultadosNovoPlataforma.value.length > 1) {
      avisar('Escolha um contato da lista “Já está no chat” antes de adicionar.');
      return;
    }
    avisar('Informe um telefone válido com DDD, ou escolha um contato do chat.');
    return;
  }
  // Atalho local: já está no funil carregado → só move / abre.
  const local = acharNoFunilPorTel(tel);
  if (local) {
    salvandoNovo.value = true;
    try {
      if (local.col.id !== col.id) {
        await apiCrmContatoMover(
          Number(accountId.value),
          currentUser.value,
          local.ct.id,
          col.id
        );
        moverContato(local.ct.id, local.col.id, col.id);
        avisar(`Já estava no CRM — movi para "${col.titulo}".`);
      } else {
        avisar('Já estava no CRM nesta etapa.');
      }
      adicionandoEm.value = null;
      resetNovoContato();
      await abrirDetalhe(col, local.ct);
    } catch (e) {
      avisar(e.message);
    } finally {
      salvandoNovo.value = false;
    }
    return;
  }
  salvandoNovo.value = true;
  const puxar = !!novoContato.jaNaPlataforma;
  try {
    const r = await apiCrmContatoCreate(Number(accountId.value), currentUser.value, {
      colunaId: col.id,
      nome: String(novoContato.nome || '').trim() || `+${tel}`,
      telefone: tel,
      iaAtiva: false,
      jaNaPlataforma: puxar,
      criarNaPlataforma: !puxar,
      plataformaId: novoContato.plataformaId || undefined,
    });
    const msg = mensagemAposSalvar({ ...r, jaNaPlataforma: puxar });
    const criado = r?.contato && r.contato.id ? mapearContato(r.contato) : null;
    adicionandoEm.value = null;
    resetNovoContato();
    // Abre o drawer na hora com o retorno da API (não depende do board achar o tel).
    if (criado?.id) {
      await abrirDetalhe(col, criado);
    }
    await recarregar();
    avisar(msg);
    // Se o drawer ainda não abriu (só jaExistia sem payload), tenta pelo telefone.
    if (!criado?.id && (r?.contato?.id || r?.jaExistia)) {
      const hit = acharNoFunilPorTel(tel);
      if (hit) await abrirDetalhe(hit.col || col, hit.ct);
    }
  } catch (e) {
    // Rede/legado: se a API ainda devolver 409, tenta recuperar no board.
    if (e.status === 409 || /j[aá] existe/i.test(String(e.message || ''))) {
      try {
        await recarregar();
        const hit = acharNoFunilPorTel(tel);
        if (hit) {
          if (hit.col.id !== col.id) {
            await apiCrmContatoMover(
              Number(accountId.value),
              currentUser.value,
              hit.ct.id,
              col.id
            );
          }
          adicionandoEm.value = null;
          resetNovoContato();
          await abrirDetalhe(col, hit.ct);
          avisar('Já estava no CRM — abri o contato.');
          return;
        }
      } catch {
        /* cai no aviso abaixo */
      }
    }
    avisar(e.message);
  } finally {
    salvandoNovo.value = false;
  }
};
const removerContato = async (col, ct) => {
  try {
    await apiCrmContatoDelete(Number(accountId.value), currentUser.value, ct.id);
    if (detalhe.value?.contato.id === ct.id) detalhe.value = null;
    await recarregar();
    avisar(`${ct.nome} removido do funil`);
  } catch (e) {
    avisar(e.message);
  }
};

const catalogoTags = ref([]);
const catalogoCampos = ref([]);

const detalhe = ref(null); // { contato, colunaId }

const selecionados = ref(new Set());
const modoSelecao = ref(false);
const apagandoSel = ref(false);
const qtdSelecionados = computed(() => selecionados.value.size);

const toggleSelecao = id => {
  const next = new Set(selecionados.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selecionados.value = next;
};
const limparSelecao = () => {
  selecionados.value = new Set();
};
const selecionandoTodos = ref(false);
const PAGE_IDS = 200;

/**
 * Busca TODOS os ids da coluna via API.
 * NÃO envia pipelineId: colunas (ex. Novos Leads) podem pertencer a outro funil
 * do que o pipeAtivoId atual — com pipeline errado a API devolve 0 contatos.
 */
const buscarIdsColuna = async col => {
  const acc = Number(accountId.value);
  const user = currentUser.value;
  const ids = new Set();
  // Já renderizados na UI entram na hora
  visiveis(col).forEach(ct => {
    if (ct?.id) ids.add(ct.id);
  });
  // Preferir o pipeline da própria coluna — pipeAtivoId pode ser
  // "Principal" enquanto a UI mistura colunas de outro funil (ex. Novos Leads).
  const pipeDaColuna = col.pipelineId || undefined;
  let offset = 0;
  for (;;) {
    const extra = await apiCrmBoard(acc, user, pipeDaColuna, {
      limit: PAGE_IDS,
      offset,
      colunaId: col.id,
      q: busca.value,
    });
    const raw = extra.contatos || [];
    if (!raw.length) break;
    raw
      .map(mapearContato)
      .filter(c => c.id && casa(c))
      .forEach(c => ids.add(c.id));
    offset += raw.length;
    if (raw.length < PAGE_IDS) break;
    if (ids.size >= 50000) break;
  }
  return [...ids];
};

const selecionarVisiveis = () => {
  const next = new Set(selecionados.value);
  colunasVisiveis.value.forEach(col => {
    visiveis(col).forEach(ct => next.add(ct.id));
  });
  selecionados.value = next;
};

const selecionarTodosDaColuna = async col => {
  if (selecionandoTodos.value) return;
  selecionandoTodos.value = true;
  try {
    const ids = await buscarIdsColuna(col);
    const next = new Set(selecionados.value);
    ids.forEach(id => next.add(id));
    selecionados.value = next;
    avisar(
      ids.length
        ? `${ids.length} contato(s) da coluna "${col.titulo}" selecionado(s)`
        : `Nenhum contato em "${col.titulo}"`
    );
  } catch (e) {
    avisar(e.message || 'Falha ao selecionar a coluna');
  } finally {
    selecionandoTodos.value = false;
  }
};

const selecionarTodosDoFunil = async () => {
  if (selecionandoTodos.value) return;
  selecionandoTodos.value = true;
  try {
    const next = new Set(selecionados.value);
    let total = 0;
    for (const col of colunasVisiveis.value) {
      const ids = await buscarIdsColuna(col);
      ids.forEach(id => next.add(id));
      total += ids.length;
    }
    selecionados.value = next;
    avisar(total ? `${total} contato(s) selecionado(s) no funil` : 'Nenhum contato para selecionar');
  } catch (e) {
    avisar(e.message || 'Falha ao selecionar o funil');
  } finally {
    selecionandoTodos.value = false;
  }
};
const removerContatosLocais = ids => {
  const set = new Set(ids);
  for (const col of pipe.value.colunas || []) {
    if (!Array.isArray(col.contatos)) continue;
    col.contatos = col.contatos.filter(c => !set.has(c.id));
  }
  if (detalhe.value && set.has(detalhe.value.contato?.id)) detalhe.value = null;
};

const acompanharJobApagar = async (jobId, total) => {
  const acc = Number(accountId.value);
  const user = currentUser.value;
  const maxTentativas = Math.max(40, Math.ceil(total / 5) + 20);
  for (let i = 0; i < maxTentativas; i++) {
    await new Promise(r => setTimeout(r, i < 3 ? 800 : 1500));
    try {
      const j = await apiCrmJob(acc, user, jobId);
      if (j.status === 'done' || j.status === 'partial' || j.status === 'failed') {
        await recarregar().catch(() => {});
        avisar(
          j.mensagem ||
            (j.erroCount
              ? `Apagados ${j.okCount}/${j.total} (falhas: ${j.erroCount})`
              : `${j.okCount} contato(s) removido(s)`)
        );
        return;
      }
    } catch {
      /* job some se ops reiniciar — board refresh cobre */
      break;
    }
  }
  await recarregar().catch(() => {});
};

const apagarSelecionados = async () => {
  const ids = [...selecionados.value];
  if (!ids.length) return;
  if (!window.confirm(`Excluir ${ids.length} contato(s) selecionado(s)?`)) return;
  apagandoSel.value = true;
  try {
    const r = await apiCrmContatoDeleteMany(
      Number(accountId.value),
      currentUser.value,
      ids
    );
    limparSelecao();
    removerContatosLocais(ids);
    const jobId = r.jobId || r.id;
    avisar(
      jobId
        ? `Remoção de ${r.total || ids.length} contato(s) agendada no CRM (Chatwoot intacto) — continua em segundo plano`
        : `${ids.length} contato(s) enviados para remoção`
    );
    if (jobId) acompanharJobApagar(jobId, r.total || ids.length);
    else await recarregar().catch(() => {});
  } catch (e) {
    avisar(e.message || 'Falha ao agendar exclusão');
  } finally {
    apagandoSel.value = false;
  }
};

const abrirDetalhe = async (col, ct) => {
  if (modoSelecao.value) {
    toggleSelecao(ct.id);
    return;
  }
  detalhe.value = { contato: ct, colunaId: col.id };
};

const fecharDetalhe = () => {
  detalhe.value = null;
};

const onDrawerSaved = async () => {
  await recarregar().catch(() => {});
};

const onDrawerRemoved = async () => {
  detalhe.value = null;
  await recarregar().catch(() => {});
};

const mudarEtapa = async (ct, deId, paraId) => {
  moverContato(ct.id, deId, paraId);
  try {
    await apiCrmContatoMover(Number(accountId.value), currentUser.value, ct.id, paraId);
    avisar(`Contato movido para "${acharColuna(paraId)?.titulo}"`);
  } catch (e) {
    avisar(e.message);
    await recarregar().catch(() => {});
  }
};

const pipeNovoNome = ref('');
const criarPipe = async () => {
  const nome = pipeNovoNome.value.trim();
  if (!nome) return;
  try {
    const r = await apiCrmPipelineCreate(Number(accountId.value), currentUser.value, nome);
    pipeNovoNome.value = '';
    const criado = r.pipeline || r;
    if (criado?.id) pipeAtivoId.value = criado.id;
    await recarregar();
    avisar(`Funil "${nome}" criado`);
  } catch (e) {
    avisar(e.message);
  }
};
const tornarPadrao = async p => {
  try {
    await apiCrmPipelinePatch(Number(accountId.value), currentUser.value, p.id, {
      principal: true,
    });
    await recarregar();
  } catch (e) {
    avisar(e.message);
  }
};
const renomearPipe = async (p, nome) => {
  if (!nome.trim() || nome.trim() === p.nome) return;
  try {
    await apiCrmPipelinePatch(Number(accountId.value), currentUser.value, p.id, {
      nome: nome.trim(),
    });
    p.nome = nome.trim();
  } catch (e) {
    avisar(e.message);
  }
};
const excluirPipe = async p => {
  if (pipelines.length <= 1) {
    avisar('É preciso manter pelo menos um funil');
    return;
  }
  const destino = pipelines.find(x => x.id !== p.id);
  try {
    await apiCrmPipelineDelete(
      Number(accountId.value),
      currentUser.value,
      p.id,
      destino.id
    );
    pipeAtivoId.value = destino.id;
    await recarregar();
    avisar(`Funil "${p.nome}" excluído`);
  } catch (e) {
    avisar(e.message);
  }
};

const arquivoRef = ref(null);
const csvCampo = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
const exportar = async () => {
  try {
    avisar('Preparando exportação…');
    const board = await apiCrmBoard(
      Number(accountId.value),
      currentUser.value,
      pipeAtivoId.value,
      { limit: 5000, q: busca.value }
    );
    const funil = mapearFunil(pipe.value, board);
    const filtrados = funil.colunas
      .filter(col => !filtro.ocultas.includes(col.id))
      .flatMap(col =>
        (col.contatos || [])
          .filter(c => casaBusca(c, busca.value, filtro.soComNome))
          .map(c => ({ contato: c, coluna: col }))
      );
    const linhas = [
      ['nome', 'telefone', 'etapa', 'funil'].join(','),
      ...filtrados.map(({ contato, coluna: col }) =>
        [contato.nome, contato.telefone, col.titulo, pipe.value.nome]
          .map(csvCampo)
          .join(',')
      ),
    ];
    const blob = new Blob([`\uFEFF${linhas.join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crm-${pipe.value.nome.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    avisar(`${filtrados.length} contato(s) exportado(s) no seu computador`);
  } catch (e) {
    avisar(e.message);
  }
};
const importar = async e => {
  const file = e.target.files?.[0];
  e.target.value = '';
  if (!file) return;
  const texto = await file.text();
  const destino = pipe.value.colunas[0];
  let n = 0;
  const linhas = texto
    .split(/\r?\n/)
    .map(l => l.split(/[;,]/).map(x => x.replace(/^"|"$/g, '').trim()));
  for (const [a = '', b = ''] of linhas) {
    const telA = a.replace(/\D/g, '');
    const telB = b.replace(/\D/g, '');
    const tel = telB.length >= 10 ? telB : telA;
    if (tel.length < 10) continue;
    const nome = telB.length >= 10 ? a : b;
    try {
      await apiCrmContatoCreate(Number(accountId.value), currentUser.value, {
        colunaId: destino.id,
        nome: nome || `+${tel}`,
        telefone: tel,
        iaAtiva: false,
      });
      n += 1;
    } catch {
      /* duplicado ou inválido — segue o arquivo */
    }
  }
  await recarregar().catch(() => {});
  avisar(n ? `${n} contato(s) importado(s) em "${destino.titulo}"` : 'Nenhum telefone válido no arquivo');
};

const abrirTags = async () => {
  abrirMenu('tags');
  try {
    const r = await apiCrmTags(Number(accountId.value), currentUser.value);
    catalogoTags.value = r.tags || [];
    if (!catalogoTags.value.length) avisar('Nenhuma tag no CRM de produção');
  } catch (e) {
    avisar(e.message);
  }
};
const abrirCampos = async () => {
  abrirMenu('campos');
  try {
    const r = await apiCrmCampos(Number(accountId.value), currentUser.value);
    catalogoCampos.value = r.campos || [];
    if (!catalogoCampos.value.length) avisar('Nenhum campo personalizado no CRM de produção');
  } catch (e) {
    avisar(e.message);
  }
};
const buscaPlataforma = ref('');
const resultadosPlataforma = ref([]);
const buscandoPlataforma = ref(false);
let platTimer;
const abrirPlataforma = () => {
  abrirMenu('plataforma');
};
const digitarPlataforma = () => {
  clearTimeout(platTimer);
  platTimer = setTimeout(async () => {
    const q = buscaPlataforma.value.trim();
    if (q.length < 2) {
      resultadosPlataforma.value = [];
      return;
    }
    buscandoPlataforma.value = true;
    try {
      const r = await apiCrmBuscarPlataforma(
        Number(accountId.value),
        currentUser.value,
        q
      );
      resultadosPlataforma.value = r.contatos || [];
    } catch (e) {
      avisar(e.message);
    } finally {
      buscandoPlataforma.value = false;
    }
  }, 280);
};
const inserirDaPlataforma = async c => {
  const tel = telDigitos(c.telefone);
  if (tel.length < 10) {
    avisar('Este contato precisa de um telefone para entrar no CRM');
    return;
  }
  const destino = pipe.value.colunas[0];
  if (!destino) {
    avisar('Crie uma coluna antes de adicionar contatos');
    return;
  }
  try {
    const local = acharNoFunilPorTel(tel);
    if (local) {
      if (local.col.id !== destino.id) {
        await apiCrmContatoMover(
          Number(accountId.value),
          currentUser.value,
          local.ct.id,
          destino.id
        );
      }
      await recarregar();
      avisar(`${c.nome} já estava no CRM`);
      fecharMenus();
      return;
    }
    const r = await apiCrmContatoCreate(Number(accountId.value), currentUser.value, {
      colunaId: destino.id,
      nome: c.nome,
      telefone: tel,
      iaAtiva: false,
      jaNaPlataforma: true,
      criarNaPlataforma: false,
    });
    await recarregar();
    avisar(
      r?.jaExistia
        ? `${c.nome} já estava no CRM`
        : `${c.nome} puxado do chat para o CRM`
    );
    fecharMenus();
  } catch (e) {
    if (e.status === 409 || /j[aá] existe/i.test(String(e.message || ''))) {
      await recarregar().catch(() => {});
      avisar(`${c.nome} já estava no CRM`);
      fecharMenus();
      return;
    }
    avisar(e.message);
  }
};

const onKeydown = e => {
  if (e.key !== 'Escape') return;
  if (menu.value || detalhe.value || renomeando.value || adicionandoEm.value) {
    e.preventDefault();
    menu.value = null;
    detalhe.value = null;
    renomeando.value = null;
    cancelarNovo();
  }
};
const onPointerDown = e => {
  if (menu.value && !e.target.closest('[data-tf-menu]')) fecharMenus();
};
onMounted(async () => {
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('pointerdown', onPointerDown, true);
  try {
    await recarregar();
  } catch (e) {
    avisar(e.message || 'Falha ao carregar o CRM de produção');
  } finally {
    carregando.value = false;
  }
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown, true);
  document.removeEventListener('pointerdown', onPointerDown, true);
});
</script>

<template>
  <TfPopupShell
    label="CRM"
    wide
    :fullscreen="fullscreen"
    :toast="toast"
    @close="emit('close')"
  >
    <div class="crm-page flex flex-col flex-1 min-h-0">
      <div class="crm-toolbar">
        <h2 class="m-0 mr-1 text-base font-bold tracking-tight">CRM</h2>
        <div class="crm-search">
          <span class="i-lucide-search size-4" />
          <input
            v-model="busca"
            class="tf-input !pl-10"
            placeholder="Buscar contatos..."
          />
        </div>
        <button
          type="button"
          class="tf-btn contorno sm"
          :class="{ 'crm-filter-on': modoSelecao }"
          @click="modoSelecao = !modoSelecao; if (!modoSelecao) limparSelecao()"
        >
          {{ modoSelecao ? 'Cancelar seleção' : 'Selecionar' }}
        </button>
        <template v-if="modoSelecao">
          <button
            type="button"
            class="tf-btn contorno sm"
            :disabled="selecionandoTodos"
            @click="selecionarTodosDoFunil"
          >
            {{ selecionandoTodos ? 'Selecionando…' : 'Selecionar todos do funil' }}
          </button>
          <button type="button" class="tf-btn ghost sm" :disabled="selecionandoTodos" @click="selecionarVisiveis">
            Só os carregados
          </button>
          <button
            type="button"
            class="tf-btn danger sm"
            :disabled="!qtdSelecionados || apagandoSel || selecionandoTodos"
            @click="apagarSelecionados"
          >
            {{ apagandoSel ? 'Apagando…' : `Apagar selecionados (${qtdSelecionados})` }}
          </button>
        </template>
        <div class="relative" data-tf-menu>
          <button
            type="button"
            class="tf-btn contorno icon"
            :class="{ 'crm-filter-on': filtroAtivo }"
            aria-label="Filtrar"
            title="Filtrar"
            @click="abrirMenu('filtro')"
          >
            <span class="i-lucide-filter size-4" />
          </button>
          <div v-if="menu === 'filtro'" class="tf-menu top-11 left-0 w-72 grid gap-3 p-3">
            <div>
              <div class="tf-section-label mb-2">Etapas visíveis</div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="col in pipe.colunas"
                  :key="col.id"
                  type="button"
                  class="crm-chip"
                  :class="{ 'is-on': !filtro.ocultas.includes(col.id) }"
                  @click="toggleOculta(col.id)"
                >
                  <span class="tf-dot" :style="{ background: col.cor }" />
                  {{ col.titulo }}
                </button>
              </div>
            </div>
            <label class="flex items-center gap-2 text-[13px] cursor-pointer">
              <input v-model="filtro.soComNome" type="checkbox" />
              Somente contatos com nome
            </label>
            <button type="button" class="tf-btn contorno sm w-full" @click="limparFiltro">
              Limpar filtros
            </button>
          </div>
        </div>
        <div class="crm-views" role="group" aria-label="Modo de visualização">
          <button
            v-for="v in VIEWS"
            :key="v.id"
            type="button"
            class="crm-view-btn"
            :class="{ 'is-active': view === v.id }"
            :aria-label="v.label"
            :aria-pressed="view === v.id"
            :title="v.label"
            @click="view = v.id"
          >
            <span class="size-4" :class="v.icon" />
          </button>
        </div>
        <template v-if="view === 'kanban'">
          <button type="button" class="tf-btn contorno icon crm-icon-btn" title="Diminuir zoom" @click="mudarZoom(-0.1)">
            <span class="i-lucide-zoom-out size-5" />
          </button>
          <button type="button" class="tf-btn contorno icon crm-icon-btn" title="Aumentar zoom" @click="mudarZoom(0.1)">
            <span class="i-lucide-zoom-in size-5" />
          </button>
        </template>

        <div class="crm-toolbar-right">
          <div class="relative flex items-center gap-1.5" data-tf-menu>
            <label class="flex items-center gap-1.5">
              <span class="text-xs font-semibold text-[var(--tf-muted)]">Funil</span>
              <select v-model="pipeAtivoId" class="tf-input !h-[34px] !w-auto min-w-[130px] max-w-[180px] !py-0" aria-label="Funil ativo">
                <option v-for="p in pipelines" :key="p.id" :value="p.id">
                  {{ p.nome }}{{ p.padrao ? ' ★' : '' }}
                </option>
              </select>
            </label>
            <button type="button" class="tf-btn contorno pill sm" @click="abrirMenu('pipes')">
              Gerenciar
            </button>
            <div v-if="menu === 'pipes'" class="tf-menu top-11 right-0 w-80 grid gap-2.5 p-3">
              <div class="flex gap-1.5">
                <input
                  v-model="pipeNovoNome"
                  class="tf-input !h-8"
                  placeholder="Nome do novo funil"
                  @keydown.enter="criarPipe"
                />
                <button type="button" class="tf-btn primary sm" @click="criarPipe">Criar</button>
              </div>
              <div
                v-for="p in pipelines"
                :key="p.id"
                class="grid gap-1.5 p-2 rounded-lg border border-solid border-[var(--tf-line)]"
              >
                <input
                  class="tf-input !h-8"
                  :value="p.nome"
                  @change="renomearPipe(p, $event.target.value)"
                />
                <div class="flex flex-wrap gap-1">
                  <button type="button" class="tf-btn ghost sm" :disabled="p.padrao" @click="tornarPadrao(p)">
                    {{ p.padrao ? '★ Padrão' : 'Tornar padrão' }}
                  </button>
                  <button type="button" class="tf-btn ghost sm" @click="pipeAtivoId = p.id; fecharMenus()">Abrir</button>
                  <button type="button" class="tf-btn ghost sm text-[var(--tf-danger)]" @click="excluirPipe(p)">Excluir</button>
                </div>
              </div>
              <p class="tf-hint text-[11px]">O funil marcado com ★ abre por padrão.</p>
            </div>
          </div>
          <button
            type="button"
            class="tf-btn contorno icon crm-icon-btn"
            :aria-label="fullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
            :title="fullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
            @click="fullscreen = !fullscreen"
          >
            <span class="size-5" :class="fullscreen ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'" />
          </button>
          <button type="button" class="tf-btn primary pill" title="Nova Coluna" @click="novaColuna">
            <span class="i-lucide-plus size-4" />
            <span class="crm-btn-label">Nova Coluna</span>
          </button>
          <button type="button" class="tf-btn contorno pill" title="Importar contatos via CSV" @click="arquivoRef?.click()">
            <span class="i-lucide-upload size-4" />
            <span class="crm-btn-label">Importar</span>
          </button>
          <input ref="arquivoRef" type="file" accept=".csv,text/csv" class="hidden" @change="importar" />
          <button type="button" class="tf-btn contorno pill" title="Exportar contatos filtrados em CSV" @click="exportar">
            <span class="i-lucide-download size-4" />
            <span class="crm-btn-label">Exportar</span>
          </button>
          <div class="relative" data-tf-menu>
            <button
              type="button"
              class="tf-btn contorno pill"
              title="Buscar contatos do chat e adicionar ao CRM"
              @click="abrirPlataforma"
            >
              <span class="i-lucide-user-plus size-4" />
              <span class="crm-btn-label">Da plataforma</span>
            </button>
            <div v-if="menu === 'plataforma'" class="tf-menu top-11 right-0 w-80 grid gap-2 p-3">
              <input
                v-model="buscaPlataforma"
                class="tf-input !h-8"
                placeholder="Nome ou telefone no chat"
                @input="digitarPlataforma"
              />
              <p class="tf-hint text-[11px]">
                Só entra no CRM quando você clicar em Adicionar. Mensagens novas não criam contato sozinhas.
              </p>
              <div v-if="buscandoPlataforma" class="tf-hint px-1">Buscando…</div>
              <div
                v-else-if="!resultadosPlataforma.length && buscaPlataforma.trim().length >= 2"
                class="tf-hint px-1"
              >
                Nenhum contato encontrado.
              </div>
              <button
                v-for="c in resultadosPlataforma"
                :key="c.id"
                type="button"
                class="tf-menu-item !items-start !flex-col gap-0.5"
                @click="inserirDaPlataforma(c)"
              >
                <span class="font-semibold">{{ c.nome }}</span>
                <span class="tf-hint">{{ c.telefone ? formatarTelefone(c.telefone) : 'sem telefone' }} · Adicionar</span>
              </button>
            </div>
          </div>
          <div class="relative" data-tf-menu>
            <button type="button" class="tf-btn contorno pill" title="Tags" @click="abrirTags">
              <span class="i-lucide-tags size-4" />
              <span class="crm-btn-label">Tags</span>
            </button>
            <div v-if="menu === 'tags'" class="tf-menu top-11 right-0 w-64 grid gap-1 p-2">
              <div v-if="!catalogoTags.length" class="tf-hint px-2 py-1">Nenhuma tag.</div>
              <div v-for="t in catalogoTags" :key="t.id" class="tf-menu-item !cursor-default">
                {{ t.nome }}
              </div>
            </div>
          </div>
          <div class="relative" data-tf-menu>
            <button type="button" class="tf-btn contorno pill" title="Campos personalizados" @click="abrirCampos">
              <span class="i-lucide-sliders-horizontal size-4" />
              <span class="crm-btn-label">Campos</span>
            </button>
            <div v-if="menu === 'campos'" class="tf-menu top-11 right-0 w-64 grid gap-1 p-2">
              <div v-if="!catalogoCampos.length" class="tf-hint px-2 py-1">Nenhum campo.</div>
              <div v-for="c in catalogoCampos" :key="c.id" class="tf-menu-item !cursor-default">
                {{ c.nome || c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="carregando" class="crm-banner">Carregando CRM de produção…</p>

      <div class="flex flex-1 min-h-0">
        <div class="flex-1 min-w-0 overflow-auto">
          <div
            v-if="view === 'kanban'"
            class="crm-board"
            :style="{ transform: `scale(${zoom})`, width: `${100 / zoom}%` }"
          >
            <div
              v-for="col in colunasVisiveis"
              :key="col.id"
              class="crm-column"
              :class="{ 'is-over': sobreColuna === col.id }"
              @dragover="onDragOver($event, col)"
              @dragleave.self="sobreColuna = null"
              @drop="onDrop($event, col)"
            >
              <div class="crm-col-stripe" :style="{ background: col.cor }" />
              <div class="crm-col-header">
                <div class="flex items-center gap-2 min-w-0 flex-1">
                  <span
                    class="crm-grip"
                    draggable="true"
                    title="Arrastar coluna"
                    @dragstart="onColDragStart($event, col)"
                    @dragend="onDragEnd"
                  >
                    <span class="i-lucide-grip-vertical size-4" />
                  </span>
                  <input
                    v-if="renomeando === col.id"
                    v-model="tituloEdit"
                    class="tf-input !h-8"
                    @keydown.enter="confirmarRenomear(col)"
                    @blur="confirmarRenomear(col)"
                  />
                  <h3 v-else class="crm-col-title" @dblclick="iniciarRenomear(col)">
                    {{ col.titulo }}
                  </h3>
                  <span class="crm-count">{{ visiveisCount(col) }}</span>
                  <button
                    v-if="modoSelecao && visiveisCount(col)"
                    type="button"
                    class="tf-btn contorno sm !h-7 !px-2 text-[11px]"
                    :disabled="selecionandoTodos"
                    :title="`Selecionar todos os ${visiveisCount(col)} contatos desta coluna`"
                    @click.stop="selecionarTodosDaColuna(col)"
                  >
                    Todos
                  </button>
                </div>
                <div class="relative" data-tf-menu>
                  <button
                    type="button"
                    class="tf-btn ghost icon sm"
                    aria-label="Menu da etapa"
                    :aria-expanded="menu === `col-${col.id}`"
                    @click="abrirMenu(`col-${col.id}`)"
                  >
                    <span class="i-lucide-ellipsis-vertical size-4" />
                  </button>
                  <div v-if="menu === `col-${col.id}`" class="tf-menu top-9 right-0">
                    <button type="button" class="tf-menu-item" @click="iniciarRenomear(col)">Renomear</button>
                    <button type="button" class="tf-menu-item" @click="abrirAdicionar(col); fecharMenus()">Adicionar contato</button>
                    <button type="button" class="tf-menu-item" @click="moverColuna(col, -1)">Mover para a esquerda</button>
                    <button type="button" class="tf-menu-item" @click="moverColuna(col, 1)">Mover para a direita</button>
                    <div class="flex flex-wrap gap-1.5 px-2.5 py-2">
                      <button
                        v-for="cor in CORES"
                        :key="cor"
                        type="button"
                        class="size-5 rounded-full border-2 border-solid"
                        :class="col.cor === cor ? 'border-[var(--tf-fg)]' : 'border-transparent'"
                        :style="{ background: cor }"
                        :title="cor"
                        @click="apiCrmColunaPatch(Number(accountId), currentUser, col.id, { cor }).then(() => { col.cor = cor; }).catch(e => avisar(e.message))"
                      />
                    </div>
                    <button type="button" class="tf-menu-item danger" @click="excluirColuna(col)">Excluir coluna</button>
                  </div>
                </div>
              </div>
              <div class="crm-col-body">
                <div v-if="adicionandoEm === col.id" class="crm-novo">
                  <p class="crm-novo-title">
                    {{ modoPuxarChat ? 'Puxar contato do chat' : 'Novo contato nesta etapa' }}
                  </p>
                  <label class="tf-field">
                    <span>Nome</span>
                    <input
                      v-model="novoContato.nome"
                      class="tf-input"
                      placeholder="Como aparece no funil"
                      autocomplete="off"
                    />
                  </label>
                  <label class="tf-field">
                    <span>Telefone</span>
                    <input
                      v-model="novoContato.telefone"
                      class="tf-input"
                      placeholder="DDD + número"
                      inputmode="tel"
                      autocomplete="off"
                      @input="onTelefoneNovoInput"
                      @keydown.enter="salvarNovoContato(col)"
                    />
                  </label>
                  <p class="tf-hint">
                    <template v-if="modoPuxarChat">
                      Selecionado do chat. O botão só coloca no CRM (não cria de novo no chat).
                      Se o telefone já existir no funil, abrimos/movemos o card.
                    </template>
                    <template v-else>
                      Preencha nome + telefone para criar no CRM e no chat — ou busque abaixo um
                      contato que já está no chat e clique nele antes de confirmar.
                    </template>
                  </p>
                  <label class="tf-field">
                    <span>Já está no chat? Buscar e selecionar</span>
                    <input
                      v-model="buscaNovoPlataforma"
                      class="tf-input"
                      placeholder="Buscar nome ou telefone"
                      autocomplete="off"
                      @input="digitarNovoPlataforma"
                    />
                  </label>
                  <div v-if="modoPuxarChat" class="crm-novo-sel">
                    Selecionado: <strong>{{ novoContato.nome || 'sem nome' }}</strong>
                    · {{ formatarTelefone(novoContato.telefone) }}
                    <button type="button" class="tf-btn ghost sm" @click="resetNovoContato">
                      Limpar seleção
                    </button>
                  </div>
                  <div v-if="buscandoNovoPlataforma" class="tf-hint">Buscando no chat…</div>
                  <div v-else-if="resultadosNovoPlataforma.length" class="crm-novo-lista">
                    <button
                      v-for="c in resultadosNovoPlataforma"
                      :key="c.id"
                      type="button"
                      class="crm-novo-hit"
                      :class="{ 'is-on': String(c.id) === String(novoContato.plataformaId) }"
                      @click="escolherDaPlataforma(col, c)"
                    >
                      <span class="crm-novo-hit-nome">{{ c.nome }}</span>
                      <span class="crm-novo-hit-tel">{{
                        c.telefone ? formatarTelefone(c.telefone) : 'sem telefone'
                      }}</span>
                      <span class="crm-novo-hit-acao">Selecionar</span>
                    </button>
                  </div>
                  <div
                    v-else-if="buscaNovoPlataforma.trim().length >= 2 && !modoPuxarChat"
                    class="tf-hint"
                  >
                    Nenhum resultado — digite outro termo ou preencha nome/telefone para criar novo.
                  </div>
                  <div class="crm-novo-acoes">
                    <button
                      type="button"
                      class="tf-btn primary sm"
                      :disabled="salvandoNovo"
                      @click="salvarNovoContato(col)"
                    >
                      {{
                        salvandoNovo
                          ? 'Salvando…'
                          : modoPuxarChat
                            ? 'Puxar para o CRM'
                            : 'Criar no CRM e no chat'
                      }}
                    </button>
                    <button type="button" class="tf-btn ghost sm" @click="cancelarNovo">Cancelar</button>
                  </div>
                </div>
                <div
                  v-for="ct in visiveis(col)"
                  :key="ct.id"
                  class="crm-card group"
                  :class="{ 'is-selected': selecionados.has(ct.id) }"
                  draggable="true"
                  role="button"
                  tabindex="0"
                  @dragstart="onCardDragStart($event, col, ct)"
                  @dragend="onDragEnd"
                  @drop.stop="onDrop($event, col, ct)"
                  @dragover="onDragOver($event, col)"
                  @click="abrirDetalhe(col, ct)"
                  @keydown.enter="abrirDetalhe(col, ct)"
                >
                  <label v-if="modoSelecao" class="crm-sel" @click.stop>
                    <input
                      type="checkbox"
                      :checked="selecionados.has(ct.id)"
                      @change="toggleSelecao(ct.id)"
                    />
                  </label>
                  <span class="crm-grip"><span class="i-lucide-grip-vertical size-4" /></span>
                  <div class="flex-1 min-w-0">
                    <p class="crm-card-nome">{{ ct.nome }}</p>
                    <p v-if="temNome(ct)" class="crm-card-meta">{{ formatarTelefone(ct.telefone, ct.ddi) }}</p>
                  </div>
                  <button
                    type="button"
                    class="crm-remove"
                    aria-label="Remover contato"
                    title="Remover contato"
                    @click.stop="removerContato(col, ct)"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>
                <button
                  v-if="restoColuna(col)"
                  type="button"
                  class="tf-btn ghost sm w-full mb-2"
                  @click="mostrarMais(col)"
                >
                  Mostrar mais {{ restoColuna(col) }}
                </button>
                <div v-if="!visiveisCount(col) && adicionandoEm !== col.id" class="crm-empty">
                  <svg class="crm-empty-art" viewBox="0 0 80 80" fill="none" aria-hidden="true">
                    <circle cx="40" cy="40" r="36" fill="var(--tf-empty-art-a)" />
                    <circle cx="40" cy="32" r="12" fill="var(--tf-empty-art-b)" />
                    <path d="M16 62c4-14 14-22 24-22s20 8 24 22" fill="var(--tf-empty-art-b)" />
                  </svg>
                  <p>Nenhum contato</p>
                  <p class="crm-empty-hint">Arraste contatos para esta coluna.</p>
                </div>
              </div>
              <div class="crm-col-footer">
                <button type="button" class="tf-btn ghost pill w-full !justify-start !h-10 text-[var(--tf-muted)]" @click="abrirAdicionar(col)">
                  <span class="i-lucide-plus size-4" />
                  Adicionar contato
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="view === 'lista'" class="p-4">
            <div class="tf-list">
              <table class="crm-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>Etapa</th>
                    <th>Criado em</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!todosVisiveis.length">
                    <td colspan="5" class="tf-list-empty">Nenhum contato encontrado.</td>
                  </tr>
                  <tr v-for="{ contato: ct, coluna: col } in todosVisiveis" :key="ct.id">
                    <td>
                      <button type="button" class="font-semibold text-left hover:underline" @click="abrirDetalhe(col, ct)">
                        {{ ct.nome }}
                      </button>
                    </td>
                    <td class="tabular-nums">{{ formatarTelefone(ct.telefone, ct.ddi) }}</td>
                    <td>
                      <select
                        class="tf-input !h-8 !w-auto !py-0"
                        :value="col.id"
                        @change="mudarEtapa(ct, col.id, $event.target.value)"
                      >
                        <option v-for="c in pipe.colunas" :key="c.id" :value="c.id">{{ c.titulo }}</option>
                      </select>
                    </td>
                    <td class="text-[var(--tf-muted)]">{{ formatarData(ct.criadoEm) }}</td>
                    <td class="text-right">
                      <button type="button" class="tf-btn ghost icon sm" title="Remover contato" @click="removerContato(col, ct)">
                        <span class="i-lucide-x size-4" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else-if="view === 'linhas'" class="grid gap-3 p-4">
            <section
              v-for="col in colunasVisiveis"
              :key="col.id"
              class="crm-row"
              :class="{ 'is-over': sobreColuna === col.id }"
              @dragover="onDragOver($event, col)"
              @drop="onDrop($event, col)"
            >
              <div class="flex items-center gap-2 px-3 py-2.5 border-0 border-b border-solid border-[var(--tf-line)]">
                <span class="tf-dot !size-2.5" :style="{ background: col.cor }" />
                <h3 class="crm-col-title">{{ col.titulo }}</h3>
                <span class="crm-count">{{ visiveisCount(col) }}</span>
              </div>
              <div class="flex gap-2 overflow-x-auto p-2.5">
                <div v-if="!visiveisCount(col)" class="tf-hint px-1 py-2">Nenhum contato.</div>
                <div
                  v-for="ct in visiveis(col)"
                  :key="ct.id"
                  class="crm-card !mb-0 w-56 flex-shrink-0"
                  :class="{ 'is-selected': selecionados.has(ct.id) }"
                  draggable="true"
                  @dragstart="onCardDragStart($event, col, ct)"
                  @dragend="onDragEnd"
                  @click="abrirDetalhe(col, ct)"
                >
                  <label v-if="modoSelecao" class="crm-sel" @click.stop>
                    <input
                      type="checkbox"
                      :checked="selecionados.has(ct.id)"
                      @change="toggleSelecao(ct.id)"
                    />
                  </label>
                  <div class="flex-1 min-w-0">
                    <p class="crm-card-nome">{{ ct.nome }}</p>
                    <p v-if="temNome(ct)" class="crm-card-meta">{{ formatarTelefone(ct.telefone, ct.ddi) }}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div v-else class="grid gap-2 p-6 max-w-[900px] mx-auto">
            <div
              v-for="col in colunasVisiveis"
              :key="col.id"
              class="flex flex-col items-center"
            >
              <div
                class="crm-funnel-bar"
                :style="{
                  width: `${Math.max(18, (visiveisCount(col) / maiorColuna) * 100)}%`,
                  background: col.cor,
                }"
              >
                <span class="truncate">{{ col.titulo }}</span>
                <span class="font-bold tabular-nums">{{ visiveisCount(col) }}</span>
              </div>
            </div>
            <p class="tf-hint text-center mt-2">
              {{ todosVisiveis.length }} contato(s) no funil "{{ pipe.nome }}"
            </p>
          </div>
        </div>

        <ContatoDrawer
          v-if="detalhe"
          :contato-id="detalhe.contato.id"
          :coluna-id="detalhe.colunaId"
          :colunas="pipe.colunas"
          :resumo="detalhe.contato"
          @close="fecharDetalhe"
          @saved="onDrawerSaved"
          @removed="onDrawerRemoved"
          @avisar="avisar"
        />
      </div>
    </div>
  </TfPopupShell>
</template>

<style scoped>
.crm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 12px 56px 12px 16px;
  border-bottom: 1px solid var(--tf-line);
  background: var(--tf-bg);
}
.crm-search {
  position: relative;
  flex: 1 1 160px;
  min-width: 0;
  max-width: 28rem;
}
.crm-search > span {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--tf-muted);
  pointer-events: none;
}
.crm-filter-on {
  color: var(--tf-primary-fg) !important;
  background: var(--tf-primary) !important;
}
.crm-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--tf-line);
  border-radius: 9999px;
  font-size: 12px;
  color: var(--tf-muted);
  opacity: 0.6;
}
.crm-chip.is-on {
  border-color: var(--tf-chip-border);
  color: var(--tf-fg);
  opacity: 1;
}
.crm-views {
  display: flex;
  border: 1px solid var(--tf-line);
  border-radius: 6px;
  overflow: hidden;
}
.crm-view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--tf-fg);
}
.crm-view-btn:hover {
  background: var(--tf-hover);
}
.crm-view-btn.is-active {
  background: var(--tf-primary);
  color: var(--tf-primary-fg);
}
.crm-toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.crm-btn-label {
  display: none;
}
@media (min-width: 1280px) {
  .crm-btn-label {
    display: inline;
  }
}
.crm-banner {
  padding: 6px 16px;
  font-size: 12px;
  color: var(--tf-warn-fg);
  background: var(--tf-warn-bg);
  border-bottom: 1px solid var(--tf-warn-border);
}
.crm-board {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-height: 100%;
  padding: 12px 16px 20px;
  transform-origin: top left;
  transition: transform 0.15s ease;
}
.crm-column {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: min(360px, 88vw);
  max-height: calc(88vh - 150px);
  border: 1px solid var(--tf-line);
  border-radius: 8px;
  background: var(--tf-column);
  box-shadow: var(--tf-card-shadow);
}
.crm-column.is-over,
.crm-row.is-over {
  outline: 2px solid var(--tf-ring);
  outline-offset: -2px;
}
.crm-col-stripe {
  height: 6px;
  flex-shrink: 0;
  border-radius: 8px 8px 0 0;
}
.crm-col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid var(--tf-line);
}
.crm-col-title {
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.crm-count {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 9999px;
  background: var(--tf-hover);
  font-size: 12px;
  font-weight: 600;
}
.crm-grip {
  display: flex;
  flex-shrink: 0;
  color: var(--tf-muted);
  cursor: grab;
}
.crm-col-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}
.crm-col-footer {
  padding: 8px;
  border-top: 1px solid var(--tf-line);
}
.crm-novo {
  display: grid;
  gap: 10px;
  width: 100%;
  min-width: 0;
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid var(--tf-line);
  border-radius: 8px;
  background: var(--tf-card);
  box-shadow: var(--tf-card-shadow);
}
.crm-novo-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}
.crm-novo .tf-input {
  width: 100%;
  min-width: 0;
  height: 36px;
  box-sizing: border-box;
}
.crm-novo-acoes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.crm-novo-acoes .tf-btn {
  flex: 1 1 calc(50% - 8px);
  min-width: 0;
  justify-content: center;
  white-space: nowrap;
}
.crm-novo-lista {
  display: grid;
  gap: 6px;
  max-height: 168px;
  overflow-y: auto;
}
.crm-novo-hit {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--tf-line);
  border-radius: 8px;
  background: var(--tf-bg);
  text-align: left;
  cursor: pointer;
}
.crm-novo-hit:hover {
  border-color: var(--tf-chip-border);
  background: var(--tf-hover);
}
.crm-novo-hit.is-on {
  border-color: color-mix(in srgb, var(--tf-primary) 45%, var(--tf-line));
  background: var(--tf-hover);
}
.crm-novo-sel {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px dashed var(--tf-line);
  background: var(--tf-hover);
  font-size: 12px;
  color: var(--tf-muted);
}
.crm-novo-sel strong {
  color: var(--tf-fg);
}
.crm-novo-hit-nome {
  grid-column: 1;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.crm-novo-hit-tel {
  grid-column: 1;
  font-size: 12px;
  color: var(--tf-muted);
}
.crm-novo-hit-acao {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--tf-primary);
  white-space: nowrap;
}
.crm-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  padding: 12px;
  border: 1px solid var(--tf-line);
  border-radius: 8px;
  background: var(--tf-card);
  box-shadow: var(--tf-card-shadow);
  cursor: grab;
  transition: border-color 0.15s ease;
}
.crm-icon-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px;
}
.crm-icon-btn .size-5 {
  width: 1.25rem;
  height: 1.25rem;
}
.crm-card.is-selected {
  outline: 2px solid #0f172a;
  background: #f8fafc;
}
.crm-sel {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
}
.crm-card:hover {
  border-color: var(--tf-chip-border);
}
.crm-card-nome {
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.crm-card-meta {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--tf-muted);
  font-variant-numeric: tabular-nums;
}
.crm-remove {
  display: inline-flex;
  padding: 2px;
  border-radius: 4px;
  color: var(--tf-muted);
  opacity: 0;
}
.crm-card:hover .crm-remove,
.crm-card:focus-within .crm-remove {
  opacity: 1;
}
.crm-remove:hover {
  color: var(--tf-danger);
  background: var(--tf-hover);
}
.crm-empty {
  padding: 32px 8px;
  text-align: center;
}
.crm-empty p {
  margin: 0;
  font-size: 14px;
  color: var(--tf-muted);
}
.crm-empty .crm-empty-hint {
  margin-top: 4px;
  font-size: 12px;
}
.crm-empty-art {
  display: block;
  width: 80px;
  height: 80px;
  margin: 0 auto 12px;
  opacity: 0.7;
}
.crm-row {
  border: 1px solid var(--tf-line);
  border-radius: 8px;
  background: var(--tf-column);
}
.crm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.crm-table th {
  padding: 10px 12px;
  border-bottom: 1px solid var(--tf-line);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
  color: var(--tf-muted);
}
.crm-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--tf-line);
}
.crm-table tbody tr:last-child td {
  border-bottom: none;
}
.crm-table tbody tr:hover {
  background: var(--tf-hover);
}
.crm-funnel-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 48px;
  padding: 0 18px;
  border-radius: 10px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: width 0.25s ease;
}
.crm-drawer {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 320px;
  flex-shrink: 0;
  padding: 16px;
  overflow-y: auto;
  border-left: 1px solid var(--tf-line);
  background: var(--tf-bg);
}
@media (max-width: 767px) {
  .crm-drawer {
    position: absolute;
    inset: 0;
    z-index: 25;
    width: auto;
    border-left: none;
  }
}
.crm-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: var(--tf-hover);
  color: var(--tf-muted);
}
.crm-det-tags {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.crm-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.crm-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--tf-line);
  background: var(--tf-hover);
  color: var(--tf-fg);
  border-radius: 9999px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.crm-tag-chip:hover:not(:disabled) {
  border-color: var(--tf-primary);
}
.crm-tag-chip:disabled {
  opacity: 0.6;
  cursor: wait;
}
.crm-tag-chip.is-add {
  background: transparent;
  border-style: dashed;
  color: var(--tf-muted);
}
.crm-tag-suggestions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--tf-line);
  border-radius: 10px;
  background: color-mix(in srgb, var(--tf-hover) 70%, transparent);
}
.crm-tag-empty {
  text-align: center;
  padding: 16px 10px;
  border: 1px dashed var(--tf-line);
  border-radius: 10px;
}
.crm-tag-empty p {
  margin: 0;
  font-size: 13px;
}
.crm-tag-add {
  display: flex;
  gap: 8px;
  align-items: center;
}
.crm-tag-add input {
  flex: 1;
  min-width: 0;
}
.tf-btn.sm {
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
}
</style>
